import { useEffect, useState } from 'react';
import { ExternalLink, Loader2 } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogMain,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ncoder/ui';
import { cn } from '@/lib/utils';
import { BILLING_PLANS } from '@/services/org-admin';
import type { PurchasablePlan } from '@/services/org-admin';
import { FormField } from './OrgAdminForm';

// Anchored below the trigger and scrolled rather than overflowing, matching InviteMemberModal —
// Radix's default item-aligned mode opens in an unpredictable direction inside a dialog.
const SELECT_CONTENT_PROPS = {
  position: 'popper' as const,
  sideOffset: 4,
  className: 'max-h-60 w-[--radix-select-trigger-width] overflow-y-auto',
};

/**
 * Plan purchase.
 *
 * The whole dialog exists to keep two numbers apart. `price_usd` is what the card is charged;
 * `budget_usd` is the LLM spend ceiling the plan grants, and they deliberately diverge (Growth costs
 * $50 and grants $100). Presenting one as the other would misrepresent what the customer is buying,
 * so each plan card labels them as two separate lines and never shows a bare figure.
 */

const PLAN_LABEL: Record<PurchasablePlan, string> = {
  starter: 'Starter',
  growth: 'Growth',
  scale: 'Scale',
};

/** A member this dialog may buy for, when the seat was not decided before opening. */
export interface PlanPurchaseSeatOption {
  userId: string;
  email: string;
  /**
   * The plan this seat holds under a LIVE subscription, or `null`.
   *
   * Deliberately not just "the plan they are on": only a LIVE subscription makes re-buying the same
   * plan the 409 this field exists to prevent, so the panel resolves that condition with the same
   * `hasLiveSubscription` the row entry point uses rather than letting this dialog infer it from a
   * plan name.
   */
  subscribedPlan: PurchasablePlan | null;
}

export interface PlanPurchaseDialogProps {
  open: boolean;
  /**
   * The seat being upgraded, when one was already chosen — i.e. the dialog was opened from that
   * member's row. Empty when it was opened from the plan catalogue, where no member is implied yet.
   */
  memberLabel: string;
  /**
   * Members to choose between. Supplied ONLY by the catalogue entry point; when it is absent the
   * dialog behaves exactly as it did before, with the seat fixed and no picker rendered.
   *
   * The catalogue needs this because "Buy Growth" names a plan and not a person, and the alternative —
   * sending the admin back to hunt for the right row in the members table — is worse than one select.
   */
  seatOptions?: PlanPurchaseSeatOption[];
  /** Pre-selects the column the admin clicked, so the dialog opens on the plan they asked for. */
  initialPlan?: PurchasablePlan;
  /**
   * The plan this seat is ALREADY on, which puts the dialog into change mode.
   *
   * Change mode is a different transaction, not a relabelled purchase: it reprices the seat's live
   * Stripe subscription and prorates the difference, where a purchase creates a new subscription
   * through Checkout. The backend refuses the second on a seat that already has the first, so the
   * two cannot be collapsed into one call — hence one prop that switches the whole dialog rather
   * than the caller passing a mode it might get wrong.
   *
   * Absent for a seat with no subscription, which is the original buy flow, unchanged.
   */
  currentPlan?: PurchasablePlan;
  isSubmitting: boolean;
  /** Set when the last attempt failed, so the dialog stays open and shows why. */
  errorMessage: string | null;
  /**
   * `userId` is passed only when the picker was used. The row entry point already knows its seat, so
   * making it round-trip through this argument would give two sources of truth for who is being
   * charged — on the one screen where charging the wrong person is unrecoverable.
   */
  onSubmit: (plan: PurchasablePlan, userId?: string) => void;
  onClose: () => void;
}

/**
 * Which way a change goes, for the CONFIRMATION COPY only.
 *
 * The server decides this authoritatively and returns it, because it reads the live price table.
 * This is a preview computed from the mirrored catalogue so the admin can read the consequence
 * before committing — it must never be treated as the outcome.
 */
function previewDirection(
  currentPlan: PurchasablePlan,
  nextPlan: PurchasablePlan,
): 'upgrade' | 'downgrade' | 'same' {
  if (currentPlan === nextPlan) return 'same';
  const current = BILLING_PLANS.find((tier) => tier.plan === currentPlan)?.priceUsd ?? 0;
  const next = BILLING_PLANS.find((tier) => tier.plan === nextPlan)?.priceUsd ?? 0;
  return next >= current ? 'upgrade' : 'downgrade';
}

function formatUsd(amount: number): string {
  return `$${amount}`;
}

export function PlanPurchaseDialog({
  open,
  memberLabel,
  seatOptions,
  initialPlan,
  currentPlan,
  isSubmitting,
  errorMessage,
  onSubmit,
  onClose,
}: PlanPurchaseDialogProps) {
  // Growth is pre-selected: it is the middle tier and the one whose budget-to-price ratio is best,
  // so it is the sane default rather than a nudge toward the most expensive option. A plan clicked in
  // the catalogue wins over that default — the admin has already said which one they want.
  const [plan, setPlan] = useState<PurchasablePlan>(initialPlan ?? 'growth');
  /** Only meaningful when `seatOptions` is supplied. Deliberately starts empty: nobody is charged by
      default, so the admin has to name the person before the button unlocks. */
  const [seatId, setSeatId] = useState('');

  const needsSeatChoice = Boolean(seatOptions);

  useEffect(() => {
    if (!open) return;
    setPlan(initialPlan ?? 'growth');
    setSeatId('');
  }, [open, initialPlan]);

  const selected = BILLING_PLANS.find((tier) => tier.plan === plan)!;
  const chosenSeat = seatOptions?.find((seat) => seat.userId === seatId) ?? null;
  /** Who the confirmation line names — the picked member, or the seat the row entry point fixed. */
  const targetLabel = needsSeatChoice ? (chosenSeat?.email ?? '') : memberLabel;
  /*
   * The plan the dialog's target already holds under a live subscription, from whichever entry point
   * supplied the target: the row passes `currentPlan`, the catalogue resolves it from the member just
   * picked. One value, so the grid, the submit gate and the preview cannot disagree about it.
   *
   * `isChange`/`direction` are derived from THIS, not from `currentPlan` alone — otherwise the
   * catalogue entry point would show "Buy a plan" / Stripe-redirect copy for a member it already
   * knows has a live subscription, right up until the submit 409s.
   */
  const heldPlan: PurchasablePlan | null = currentPlan ?? chosenSeat?.subscribedPlan ?? null;
  const isChange = Boolean(heldPlan);
  const direction = heldPlan ? previewDirection(heldPlan, plan) : null;

  // Submitting the plan already held is a no-op the backend rejects with 409. Cheaper and clearer to
  // block it here than to round-trip for that error — and `plan !== heldPlan` is what covers the
  // catalogue path, where the plan was pre-selected before the member was known.
  const canSubmit =
    !isSubmitting &&
    (!needsSeatChoice || Boolean(chosenSeat)) &&
    direction !== 'same' &&
    plan !== heldPlan;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && !isSubmitting && onClose()}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold">
            {isChange ? 'Change plan' : 'Buy a plan'}
          </DialogTitle>
          <DialogDescription className="text-[13px]">
            {isChange ? (
              <>
                For <span className="font-medium text-foreground">{targetLabel}</span>. This changes
                the existing subscription — you will not be sent to Stripe.
              </>
            ) : needsSeatChoice ? (
              'A plan applies to one member. Choose who this is for — you will be taken to Stripe to pay.'
            ) : (
              <>
                For <span className="font-medium text-foreground">{memberLabel}</span>. You will be
                taken to Stripe to pay.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <DialogMain className="space-y-4">
          {/* First, because it is the question the catalogue left open, and because every figure
              below is about to be attributed to whoever is named here. */}
          {needsSeatChoice && (
            <FormField label="Member" required>
              {({ id, ...fieldProps }) => (
                <Select value={seatId} onValueChange={setSeatId} disabled={isSubmitting}>
                  <SelectTrigger id={id} {...fieldProps} className="h-9 text-[13px]">
                    <SelectValue placeholder="Choose a member…" />
                  </SelectTrigger>
                  <SelectContent {...SELECT_CONTENT_PROPS}>
                    {seatOptions!.map((seat) => (
                      <SelectItem key={seat.userId} value={seat.userId} className="text-[13px]">
                        {seat.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </FormField>
          )}

          <fieldset>
            <legend className="oa-micro mb-2">Plan</legend>
            {/*
              `auto-fit` rather than three fixed columns: on a phone the cards stack instead of
              squeezing three price comparisons into 300px.
            */}
            <div
              role="radiogroup"
              aria-label="Plan"
              className="grid gap-2 [grid-template-columns:repeat(auto-fit,minmax(min(150px,100%),1fr))]"
            >
              {BILLING_PLANS.map((tier) => {
                const isSelected = tier.plan === plan;
                /*
                 * The plan already held is not offerable. Taken out of the radio group entirely rather
                 * than left selectable with the submit greyed out afterwards — a reader should not have
                 * to press something to discover it was never available.
                 */
                const isHeld = tier.plan === heldPlan;
                return (
                  <button
                    key={tier.plan}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    disabled={isSubmitting || isHeld}
                    onClick={() => setPlan(tier.plan)}
                    className={cn(
                      'oa-interactive rounded-[var(--oa-radius-md)] border p-3 text-left',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                      isHeld
                        ? 'cursor-not-allowed border-border/70 bg-muted/30 opacity-60'
                        : isSelected
                          ? 'border-primary/60 bg-primary/[0.06]'
                          : 'border-border/70 hover:border-border hover:bg-muted/40',
                    )}
                  >
                    <span className="block text-[13px] font-semibold">
                      {PLAN_LABEL[tier.plan]}
                      {/* Naming the current tier in the grid is what makes the change legible as a
                          move FROM somewhere — without it the dialog looks like a fresh purchase. */}
                      {isHeld && (
                        <span className="ml-1.5 font-normal text-muted-foreground">
                          · current plan
                        </span>
                      )}
                    </span>
                    {/*
                      Two labelled lines, never one number. "You pay" and "AI budget" are different
                      quantities and the reader has to be able to tell which is which at a glance.
                    */}
                    <span className="mt-2 block">
                      <span className="oa-micro block">You pay</span>
                      <span className="block text-[15px] font-semibold tabular-nums">
                        {formatUsd(tier.priceUsd)}
                        <span className="text-[11px] font-normal text-muted-foreground">
                          /mo
                        </span>
                      </span>
                    </span>
                    <span className="mt-1.5 block">
                      <span className="oa-micro block">AI budget</span>
                      <span className="block text-[15px] font-semibold tabular-nums">
                        {formatUsd(tier.budgetUsd)}
                        <span className="text-[11px] font-normal text-muted-foreground">
                          /mo
                        </span>
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </fieldset>

          {/* The confirmation restates both figures together, so the last thing read before paying
              is the full trade rather than a total. */}
          <div className="rounded-[var(--oa-radius-md)] border border-border/60 bg-muted/30 p-3 text-[13px]">
            {isChange ? (
              <p>
                <span className="font-medium">{targetLabel}</span> moves from{' '}
                <span className="font-medium">{PLAN_LABEL[currentPlan!]}</span> to{' '}
                <span className="font-medium">{PLAN_LABEL[selected.plan]}</span> —{' '}
                <span className="font-semibold tabular-nums">{formatUsd(selected.priceUsd)}</span> per
                month, with a{' '}
                <span className="font-semibold tabular-nums">{formatUsd(selected.budgetUsd)}</span>{' '}
                AI budget each cycle.{' '}
                {/*
                  The two directions settle differently and the difference matters: an upgrade takes
                  money now, a downgrade does NOT return any. Saying "refund" where Stripe issues a
                  credit is the single most likely way for this screen to mislead someone.
                */}
                {direction === 'upgrade'
                  ? 'The difference for the rest of this billing period is charged now.'
                  : 'The unused difference is credited against the next invoice — it is not refunded.'}
              </p>
            ) : (
              <p>
                <span className="font-medium">{PLAN_LABEL[selected.plan]}</span> for{' '}
                <span className="font-medium">
                  {targetLabel || 'the member you choose above'}
                </span>{' '}
                — <span className="font-semibold tabular-nums">{formatUsd(selected.priceUsd)}</span>
                 per month, granting a{' '}
                <span className="font-semibold tabular-nums">{formatUsd(selected.budgetUsd)}</span> AI
                spending budget
                 each cycle.
              </p>
            )}
          </div>

          {errorMessage && (
            <p role="alert" className="text-[13px] font-medium text-destructive-strong">
              {errorMessage}
            </p>
          )}
        </DialogMain>

        <DialogFooter>
          <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          {/* Disabled while pending, which is the duplicate-submit guard: a second Checkout session
              for the same seat would be a second thing for the admin to reconcile. */}
          <Button
            type="button"
            size="sm"
            onClick={() => onSubmit(plan, needsSeatChoice ? seatId : undefined)}
            disabled={!canSubmit}
          >
            {isSubmitting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            ) : (
              // No external-link affordance in change mode — the browser stays here, and promising
              // a hand-off that does not happen is a small lie the icon would tell on its own.
              !isChange && <ExternalLink className="h-3.5 w-3.5" aria-hidden />
            )}
            {isChange
              ? isSubmitting
                ? 'Changing…'
                : direction === 'downgrade'
                  ? 'Downgrade plan'
                  : 'Upgrade plan'
              : isSubmitting
                ? 'Opening Stripe…'
                : 'Continue to payment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
